// Iter 128b — Compact shift pill.
// Layout principles:
//   • The pill renders a clean info block (time, role, site) only.
//   • Manager actions (Publish, Cancel/Delete) are hover-only and live at
//     the top-right of the pill — they no longer compete with the cell's
//     "+ Add shift" footer for the bottom-right corner.
//   • Pill is draggable for managers only; a faint grip icon sits at the
//     top-left so it's discoverable without dominating the pill.
import { useState } from 'react';
import axios from 'axios';
import { Trash2, GripVertical, Send } from 'lucide-react';
import { API } from './_helpers';

export default function ShiftPill({ shift, isManager, meId, site, onChange, onPropose, onDragStartShift, onDragEndShift }) {
  const [busy, setBusy] = useState(false);
  const bg = shift.status === 'draft' ? '#cbd5e1'
    : shift.acceptance === 'accepted' ? '#15803d'
    : shift.acceptance === 'declined' ? '#dc2626'
    : '#f59e0b';
  const fg = '#fff';

  const publish = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try { await axios.post(`${API}/roster/shifts/${shift.shift_id}/publish`, null, { withCredentials: true }); onChange(); }
    catch (err) { alert(err?.response?.data?.detail || 'Publish failed'); }
    setBusy(false);
  };

  const del = async (e) => {
    e?.stopPropagation();
    const msg = shift.status === 'published'
      ? `Cancel this published shift?\n\n${shift.user_name} will be emailed that the shift is cancelled.`
      : `Delete this ${shift.status} shift?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try { await axios.delete(`${API}/roster/shifts/${shift.shift_id}`, { withCredentials: true }); onChange(); }
    catch (err) { alert(err?.response?.data?.detail || 'Delete failed'); }
    setBusy(false);
  };

  const onDragStart = (e) => {
    if (!isManager) return;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', shift.shift_id); } catch { /* ignore */ }
    onDragStartShift?.(shift);
  };
  const onDragEnd = () => onDragEndShift?.();

  const isMine = !isManager && shift.user_id === meId;
  const canSwap = isMine && shift.status === 'published';
  const deleteLabel = shift.status === 'published' ? 'Cancel shift + email staff' : 'Delete shift';

  return (
    <div className="rounded text-xs px-1.5 py-1 mb-1 leading-tight relative group/pill overflow-hidden"
         draggable={isManager}
         onDragStart={onDragStart}
         onDragEnd={onDragEnd}
         style={{ background: bg, color: fg, cursor: isManager ? 'grab' : 'default' }}
         data-testid={`shift-pill-${shift.shift_id}`}>
      {/* Site indicator — left-edge colour stripe (replaces the old top-right
          dot so the top-right can host the hover action cluster). */}
      {site && (
        <span className="absolute top-0 left-0 bottom-0 w-1 pointer-events-none"
              style={{ background: site.color }} title={site.name} />
      )}
      {isManager && (
        <GripVertical className={`absolute top-0.5 ${site ? 'left-1' : 'left-0'} w-3 h-3 opacity-30 group-hover/pill:opacity-70 transition-opacity pointer-events-none`} aria-hidden />
      )}

      {/* Hover-only action cluster — top-right corner of the pill. */}
      {isManager && (
        <div className="absolute top-0.5 right-0.5 flex items-center gap-0.5 opacity-0 group-hover/pill:opacity-100 transition-opacity bg-black/20 backdrop-blur-sm rounded px-0.5 py-0.5 z-10">
          {shift.status === 'draft' && (
            <button onClick={publish} disabled={busy} title="Publish + email staff"
                    data-testid={`shift-publish-${shift.shift_id}`}
                    className="p-1 rounded hover:bg-white/30 disabled:opacity-50">
              <Send className="w-3 h-3" />
            </button>
          )}
          <button onClick={del} disabled={busy} title={deleteLabel}
                  data-testid={`shift-delete-${shift.shift_id}`}
                  className="p-1 rounded hover:bg-white/30 disabled:opacity-50">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className={`font-bold ${isManager ? 'pl-2.5' : site ? 'pl-1.5' : ''}`}>{shift.start}–{shift.end}</div>
      {shift.role_label && <div className={`opacity-90 text-[10px] truncate ${site ? 'pl-1.5' : ''}`}>{shift.role_label}</div>}
      {site && <div className="opacity-90 text-[10px] truncate font-semibold pl-1.5">{site.short_code || site.name}</div>}
      {!site && shift.location && <div className="opacity-75 text-[10px] truncate">{shift.location}</div>}

      {/* Iter 130 — Clock-in/out timestamps from the user's timesheet. Show
          only if at least one of actual_start / actual_end is set. */}
      {(shift.actual_start || shift.actual_end) && (
        <div className={`mt-0.5 inline-flex items-center gap-1 text-[10px] font-mono bg-black/20 rounded px-1 py-px ${site ? 'ml-1.5' : ''}`}
             data-testid={`shift-clock-${shift.shift_id}`}
             title={`Clocked${shift.timesheet_status ? ' · ' + shift.timesheet_status : ''}`}>
          <span style={{ opacity: 0.7 }}>⏱</span>
          <span>{shift.actual_start || '--:--'}–{shift.actual_end || '--:--'}</span>
        </div>
      )}

      {canSwap && onPropose && (
        <button onClick={() => onPropose()} data-testid={`shift-swap-${shift.shift_id}`}
                title="Give away or swap this shift"
                className={`text-[10px] underline mt-0.5 inline-flex items-center gap-0.5 ${site ? 'pl-1.5' : ''}`}>
          ⇄ Swap / give away
        </button>
      )}
    </div>
  );
}
